/**
 * Übungen lesen und schreiben — **immer lokal zuerst**.
 *
 * Eine Übung gehört dem Nutzer: Der Startbestand wird beim Anlegen des Kontos
 * kopiert, ab da ist jede Zeile bearbeitbar. Varianten hängen über `parentId`
 * an ihrem Grundtyp und sind die Einheit, in der geloggt wird.
 */

import { newId } from '@kraftwerk/shared';
import type { ExerciseVideo, Muscle, TrackingMode } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalExercise } from './local.js';
import { stamp, writeRow, writeRows } from './write.js';

export interface ExerciseDraft {
  name: string;
  parentId?: string | null;
  trackingMode?: TrackingMode;
  equipment?: LocalExercise['equipment'];
  primaryMuscles?: Muscle[];
  secondaryMuscles?: Muscle[];
  movementPattern?: LocalExercise['movementPattern'];
  bodyRegion?: LocalExercise['bodyRegion'];
  isCompound?: boolean;
  isUnilateral?: boolean;
  variantLabel?: string | null;
  defaultSets?: number;
  repMin?: number;
  repMax?: number;
  incrementKg?: number;
  restSeconds?: number;
  videos?: ExerciseVideo[];
  cues?: string[];
  setupNotes?: string | null;
  note?: string | null;
  isFavorite?: boolean;
  sortOrder?: number;
}

function emptyExercise(id: string, now: string): LocalExercise {
  return {
    id,
    updatedAt: now,
    deletedAt: null,
    parentId: null,
    catalogKey: null,
    name: '',
    variantLabel: null,
    trackingMode: 'weightReps',
    equipment: 'other',
    primaryMuscles: [],
    secondaryMuscles: [],
    movementPattern: 'isolation',
    bodyRegion: 'fullBody',
    isCompound: false,
    isUnilateral: false,
    defaultSets: 3,
    repMin: 8,
    repMax: 12,
    incrementKg: 2.5,
    restSeconds: 90,
    videos: [],
    cues: [],
    setupNotes: null,
    note: null,
    isFavorite: false,
    sortOrder: 0,
  };
}

export async function listExercises(database: KraftwerkDatabase = db): Promise<LocalExercise[]> {
  const rows = await database.exercises.toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort(
      (a, b) =>
        Number(b.isFavorite) - Number(a.isFavorite) ||
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name, 'de'),
    );
}

export async function getExercise(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<LocalExercise | null> {
  const row = await database.exercises.get(id);
  return row && !row.deletedAt ? row : null;
}

export interface ExerciseFamily {
  root: LocalExercise;
  variants: LocalExercise[];
}

/**
 * Übungen nach Familien gruppiert — Grundtyp mit seinen Varianten.
 *
 * Eine Variante, deren Grundtyp gelöscht wurde, steht als eigene Familie da.
 * Sie verschwinden zu lassen wäre der schlimmere Fehler: An ihr hängt der
 * Verlauf.
 */
export function groupIntoFamilies(rows: readonly LocalExercise[]): ExerciseFamily[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const families = new Map<string, ExerciseFamily>();

  for (const row of rows) {
    const hasParent = row.parentId !== null && byId.has(row.parentId);
    if (!hasParent) families.set(row.id, { root: row, variants: [] });
  }

  for (const row of rows) {
    if (row.parentId === null) continue;
    const family = families.get(row.parentId);
    if (family) family.variants.push(row);
    // Grundtyp gelöscht: Die Variante wird selbst zur Wurzel — siehe oben.
    else if (!families.has(row.id)) families.set(row.id, { root: row, variants: [] });
  }

  for (const family of families.values()) {
    family.variants.sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }

  return [...families.values()].sort((a, b) => a.root.name.localeCompare(b.root.name, 'de'));
}

/** Der Anzeigename in einer Zeile: Kurzform, wo es eine gibt. */
export function shortLabel(exercise: LocalExercise): string {
  return exercise.variantLabel?.trim() || exercise.name;
}

export async function createExercise(
  draft: ExerciseDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalExercise> {
  const now = stamp();
  // Die Kennung entsteht **hier**, nicht auf dem Server — sonst könnte offline
  // nichts angelegt werden, auf das andere Zeilen verweisen.
  const row: LocalExercise = {
    ...emptyExercise(newId(), now),
    ...draft,
    name: draft.name.trim(),
    parentId: draft.parentId ?? null,
    variantLabel: draft.variantLabel ?? null,
    setupNotes: draft.setupNotes ?? null,
    note: draft.note ?? null,
  };

  return writeRow('exercises', row, 'upsert', database);
}

/**
 * Eine Variante anlegen — vorbelegt mit den Werten des Grundtyps.
 *
 * **Kopie, keine Vererbung:** Was hier übernommen wird, gehört danach der
 * Variante. Ändert sich der Grundtyp später, ändert sich die Variante nicht
 * hinter dem Rücken des Nutzers.
 *
 * Ausgenommen sind `videos` und `cues`: Sie bleiben leer und werden in der
 * Oberfläche vom Grundtyp geliehen — sichtbar beschriftet, nicht heimlich.
 */
export async function createVariant(
  parentId: string,
  draft: Pick<ExerciseDraft, 'name' | 'variantLabel'> & Partial<ExerciseDraft>,
  database: KraftwerkDatabase = db,
): Promise<LocalExercise | null> {
  const parent = await getExercise(parentId, database);
  if (!parent) return null;
  // Genau eine Ebene: Die Variante einer Variante hängt an deren Grundtyp.
  const rootId = parent.parentId ?? parent.id;

  const now = stamp();
  const row: LocalExercise = {
    ...parent,
    id: newId(),
    updatedAt: now,
    deletedAt: null,
    parentId: rootId,
    catalogKey: null,
    videos: [],
    cues: [],
    isFavorite: false,
    ...draft,
    name: (draft.name ?? parent.name).trim(),
    variantLabel: draft.variantLabel ?? null,
  };

  return writeRow('exercises', row, 'upsert', database);
}

export async function updateExercise(
  id: string,
  changes: Partial<ExerciseDraft>,
  database: KraftwerkDatabase = db,
): Promise<LocalExercise | null> {
  const existing = await database.exercises.get(id);
  if (!existing || existing.deletedAt) return null;

  const row: LocalExercise = { ...existing, ...changes, updatedAt: stamp() };
  return writeRow('exercises', row, 'upsert', database);
}

/**
 * Weich löschen.
 *
 * Varianten werden **nicht** mitgelöscht: An ihnen hängt der Verlauf des
 * Nutzers. Sie verlieren nur ihren Grundtyp und stehen danach für sich — die
 * Oberfläche sagt das ausdrücklich.
 */
export async function deleteExercise(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.exercises.get(id);
  if (!existing || existing.deletedAt) return;

  const now = stamp();
  const orphans = (await database.exercises.where('parentId').equals(id).toArray()).filter(
    (row) => !row.deletedAt,
  );

  await writeRows(
    [
      { table: 'exercises', row: { ...existing, deletedAt: now, updatedAt: now }, kind: 'delete' },
      ...orphans.map((row) => ({
        table: 'exercises' as const,
        row: { ...row, parentId: null, updatedAt: now },
      })),
    ],
    database,
  );
}

/**
 * Videos und Hinweise: eigene, sonst die des Grundtyps — mit Herkunft.
 *
 * Die Herkunft steht je Feld, weil sie sich unterscheiden kann: eigene Hinweise,
 * geliehenes Video. Ein gemeinsames Kennzeichen würde eines von beidem falsch
 * beschriften.
 */
export interface InheritedGuidance {
  videos: ExerciseVideo[];
  cues: string[];
  /** Name des Grundtyps, wenn die Videos geliehen sind. Sonst `null`. */
  videosFrom: string | null;
  /** Name des Grundtyps, wenn die Hinweise geliehen sind. Sonst `null`. */
  cuesFrom: string | null;
}

export function guidanceFor(
  exercise: LocalExercise,
  parent: LocalExercise | null | undefined,
): InheritedGuidance {
  // Je Feld einzeln entscheiden: Wer der Variante eigene Hinweise gibt, verliert
  // sonst das Video des Grundtyps — und die Oberfläche behauptete „kein Video
  // hinterlegt", obwohl eines existiert.
  const borrowVideos = exercise.videos.length === 0 && (parent?.videos.length ?? 0) > 0;
  const borrowCues = exercise.cues.length === 0 && (parent?.cues.length ?? 0) > 0;

  return {
    videos: borrowVideos ? (parent?.videos ?? []) : exercise.videos,
    cues: borrowCues ? (parent?.cues ?? []) : exercise.cues,
    videosFrom: borrowVideos ? (parent?.name ?? null) : null,
    cuesFrom: borrowCues ? (parent?.name ?? null) : null,
  };
}
